import { useMutation } from '@apollo/client';
import { EEventFormFieldType } from '__generated__/globalTypes';
import { IconButton } from 'elements/eventView/atomic/IconButton';
import { EventTextInput } from 'elements/eventView/inputs/EventTextInput';
import { SubmitForm, SubmitFormVariables } from 'graphql/mutation/__generated__/SubmitForm';
import { E_SUBMIT_FORM_MUTATION } from 'graphql/mutation/guideMutation';
import { EFonts } from 'libs/styles/styling';
import { VALID_EMAIL_HASH } from 'libs/utils/validation/validation';
import { useContext, useEffect, useState } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';
import { useLocalStorage } from 'usehooks-ts';

interface Props {
    className?: string;
}

export const HomepageNewsletterMobile = (props: Props): JSX.Element => {
    const ctxEventView = useContext(CtxEventView);

    const newsletter = ctxEventView.eventData.eventInfo.newsletter;

    const FORM_SUBMITTED_KEY = 'guide_newsletterSubmitted_' + newsletter?.formId;
    const [isSubmitted, setIsSubmitted] = useLocalStorage(FORM_SUBMITTED_KEY, false);
    const [email, setEmail] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const [submitFormMutation] = useMutation<SubmitForm, SubmitFormVariables>(
        E_SUBMIT_FORM_MUTATION
    );

    const form = ctxEventView.forms.find((e) => e.eventFormId === newsletter?.formId);
    const emailField = form?.fields?.find((e) => e.type === EEventFormFieldType.EMAIL);

    const emailTrimmed = email.trim();
    const isValidEmail = emailTrimmed.match(VALID_EMAIL_HASH);

    function submit() {
        const emailTrimmed = email.trim();

        if (!isValidEmail) {
            setError('Please enter a valid email address.');
            return;
        }

        submitFormMutation({
            variables: {
                eventFormId: newsletter.formId,
                eventId: ctxEventView.eventData.guideId,
                data: {
                    [emailField.fieldId]: emailTrimmed
                }
            },
            onError: () => {
                setError('Unknown erorr happened, please try again later.');
            },
            onCompleted: () => {
                setIsSubmitted(true);
                setEmail('');
            }
        });
    }

    useEffect(() => {
        if (error && isValidEmail) {
            setError(null);
        }
    }, [error, email]);

    if (!newsletter || !form || !emailField) {
        return null;
    }

    return (
        <S_HomepageNewsletterMobile
            className={props.className}
            $color={newsletter.color}
            $backgroundColor={newsletter.backgroundColor}
        >
            {!isSubmitted && (
                <>
                    <S_Title $color={newsletter.color}>{newsletter.title}</S_Title>
                    <S_Description $color={newsletter.color}>
                        {newsletter.description}
                    </S_Description>

                    <S_InputRow>
                        <S_Input
                            value={email}
                            onChange={setEmail}
                            placeholder={Localization.EMAIL}
                        />
                        <S_SubmitButton
                            iconName="arrow-right"
                            backgroundColor={newsletter.buttonBgColor}
                            onClick={submit}
                        />
                        {error && <S_Error>{error}</S_Error>}
                    </S_InputRow>
                </>
            )}
            {isSubmitted && (
                <S_Submitted>
                    <S_ThankYouNote $color={newsletter.color}>
                        {newsletter.thankYouText}
                    </S_ThankYouNote>
                    <S_SubmitAgain
                        $color={newsletter.color}
                        onClick={() => {
                            setIsSubmitted(false);
                        }}
                    >
                        {Localization.SUBMIT_AGAIN}
                    </S_SubmitAgain>
                </S_Submitted>
            )}
        </S_HomepageNewsletterMobile>
    );
};

const S_HomepageNewsletterMobile = styled.div<{ $backgroundColor: string; $color: string }>`
    width: 100%;
    padding: 20px 25px 40px 25px;
    background-color: ${(props) => props.$backgroundColor};
    border: 1px solid ${(props) => props.$color};
    border-radius: 27px;
    display: flex;
    flex-direction: column;
`;

const S_Title = styled.div<{ $color: string }>`
    color: ${(props) => props.$color};
    font-family: ${EFonts.Bold};
    font-size: 15px;
`;

const S_Description = styled.div<{ $color: string }>`
    margin-top: 10px;
    font-family: ${EFonts.Medium};
    font-size: 14px;
    color: ${(props) => props.$color};
`;

const S_Input = styled(EventTextInput)``;

const S_InputRow = styled.div`
    margin-top: 20px;
    position: relative;
`;

const S_SubmitButton = styled(IconButton)`
    position: absolute;
    right: 2px;
    top: 2px;
`;

const S_Submitted = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_ThankYouNote = styled.div<{ $color: string }>`
    color: ${(props) => props.$color};
    font-family: ${EFonts.Medium};
    font-size: 15px;
`;

const S_SubmitAgain = styled.div<{ $color: string }>`
    margin-top: 8px;
    align-self: flex-end;
    text-decoration: underline;
    font-family: ${EFonts.Regular};
    padding: 5px;
    font-size: 14px;
    color: ${(props) => props.$color};
`;

const S_Error = styled.div`
    color: #fff;
    font-size: 11px;
    font-family: ${EFonts.Regular};
    position: absolute;
    padding: 7px 20px;
`;
